import confluenceEnterpriseSku from './confluence-enterprise-sku.json';
import confluenceFree from './confluence-free.json';
import confluencePremium from './confluence-premium.json';
import confluenceStandard from './confluence-standard.json';
import jiraProductDiscoveryEnterprise from './jira-product-discovery-enterprise.json';
import jiraProductDiscoveryPremiumSku from './jira-product-discovery-premium-sku.json';
import jiraProductDiscoveryStandard from './jira-product-discovery-standard.json';
import jiraServicedeskEnterpriseSku from './jira-servicedesk-enterprise-sku.json';
import jiraServicedeskFree from './jira-servicedesk-free.json';
import jiraServicedeskPremium from './jira-servicedesk-premium.json';
import jiraServicedeskStandard from './jira-servicedesk-standard.json';
import jiraSoftwareEnterpriseSku from './jira-software-enterprise-sku.json';
import jiraSoftwareFree from './jira-software-free.json';
import jiraSoftwarePremium from './jira-software-premium.json';
import jiraSoftwareStandard from './jira-software-standard.json';
import serviceCollectionEnterprise from './service-collection-enterprise.json';
import serviceCollectionFree from './service-collection-free.json';
import serviceCollectionPremium from './service-collection-premium.json';
import serviceCollectionStandard from './service-collection-standard.json';
import teamworkCollectionEnterpriseSku from './teamwork-collection-enterprise-sku.json';
import teamworkCollectionFreeSku from './teamwork-collection-free-sku.json';
import teamworkCollectionPremiumSku from './teamwork-collection-premium-sku.json';
import teamworkCollectionStandardSku from './teamwork-collection-standard-sku.json';

export type BillingCycle = 'MONTHLY' | 'ANNUAL';

export interface GetAllowanceArgs {
	sku: string;
	meter: string;
	billingCycle: BillingCycle;
}

export const getAllowance = ({ sku, meter, billingCycle }: GetAllowanceArgs): number | undefined =>
	allowanceIndex[sku]?.[meter]?.[billingCycle];

interface EntitlementTemplate {
	features: {
		value?: {
			allowances?: {
				config: {
					usageKey: string;
					cycles: { cycle: string; amount: number }[];
				};
			}[];
		};
	}[];
}

// Keyed by SKU (`<product>-<edition>`), which is how the entitlement files are named.
const templatesBySku: Record<string, EntitlementTemplate> = {
	'confluence-enterprise-sku': confluenceEnterpriseSku,
	'confluence-free': confluenceFree,
	'confluence-premium': confluencePremium,
	'confluence-standard': confluenceStandard,
	'jira-product-discovery-enterprise': jiraProductDiscoveryEnterprise,
	'jira-product-discovery-premium-sku': jiraProductDiscoveryPremiumSku,
	'jira-product-discovery-standard': jiraProductDiscoveryStandard,
	'jira-servicedesk-enterprise-sku': jiraServicedeskEnterpriseSku,
	'jira-servicedesk-free': jiraServicedeskFree,
	'jira-servicedesk-premium': jiraServicedeskPremium,
	'jira-servicedesk-standard': jiraServicedeskStandard,
	'jira-software-enterprise-sku': jiraSoftwareEnterpriseSku,
	'jira-software-free': jiraSoftwareFree,
	'jira-software-premium': jiraSoftwarePremium,
	'jira-software-standard': jiraSoftwareStandard,
	'service-collection-enterprise': serviceCollectionEnterprise,
	'service-collection-free': serviceCollectionFree,
	'service-collection-premium': serviceCollectionPremium,
	'service-collection-standard': serviceCollectionStandard,
	'teamwork-collection-enterprise-sku': teamworkCollectionEnterpriseSku,
	'teamwork-collection-free-sku': teamworkCollectionFreeSku,
	'teamwork-collection-premium-sku': teamworkCollectionPremiumSku,
	'teamwork-collection-standard-sku': teamworkCollectionStandardSku,
};

// sku -> meter (usageKey) -> billing cycle -> allowance amount.
const buildIndex = (): Record<string, Record<string, Partial<Record<BillingCycle, number>>>> => {
	const index: Record<string, Record<string, Partial<Record<BillingCycle, number>>>> = {};

	for (const [sku, template] of Object.entries(templatesBySku)) {
		const meterIndex: Record<string, Partial<Record<BillingCycle, number>>> = {};

		for (const feature of template.features) {
			for (const allowance of feature.value?.allowances ?? []) {
				const { usageKey, cycles } = allowance.config;
				const cycleIndex = (meterIndex[usageKey] ??= {});
				for (const cycle of cycles) {
					cycleIndex[cycle.cycle as BillingCycle] = cycle.amount;
				}
			}
		}

		index[sku] = meterIndex;
	}

	return index;
};

const allowanceIndex = buildIndex();
